import {gql} from '@apollo/client'

const AUTH_SESSION_FIELDS = `
  payload
  token
  settings
  refreshToken
  refreshExpiresIn
  permissions
  groups
  user {
    id
    email
    firstName
    lastName
    avatar
    twoFactorEnabled
    twoFactorRequired
    twoFactorEmailFallbackEnabled
    passkeyCount
    primaryGroup {
      id
      name
    }
    organization {
      id
      name
      logo
      templateLogo
    }
    organizationTemplateLogo
  }
`

const PASSKEY_CREDENTIAL_FIELDS = `
  id
  name
  created
  lastUsedAt
  transports
  authenticatorAttachment
`

export const LOGIN = gql`
  mutation tokenAuth($email: String!, $password: String!) {
    tokenAuth(email: $email, password: $password) {
      ${AUTH_SESSION_FIELDS}
    }
  }
`

export const BEGIN_LOGIN = gql`
  mutation beginLogin($email: String!, $password: String!) {
    beginLogin(email: $email, password: $password) {
      requiresTwoFactor
      challengeId
      availableMethods
      emailDestination
      message
      auth {
        ${AUTH_SESSION_FIELDS}
      }
    }
  }
`

export const SEND_EMAIL_TWO_FACTOR_CODE = gql`
  mutation sendEmailTwoFactorCode($challengeId: String!) {
    sendEmailTwoFactorCode(challengeId: $challengeId) {
      success
      message
      retryAfter
      emailDestination
      expiresAt
    }
  }
`

export const COMPLETE_LOGIN = gql`
  mutation completeLogin($challengeId: String!, $code: String!, $method: String) {
    completeLogin(challengeId: $challengeId, code: $code, method: $method) {
      message
      auth {
        ${AUTH_SESSION_FIELDS}
      }
    }
  }
`

export const BEGIN_PASSKEY_REGISTRATION = gql`
  mutation beginPasskeyRegistration($name: String) {
    beginPasskeyRegistration(name: $name) {
      success
      message
      challengeId
      publicKeyOptions
    }
  }
`

export const COMPLETE_PASSKEY_REGISTRATION = gql`
  mutation completePasskeyRegistration($challengeId: String!, $credential: GenericScalar!) {
    completePasskeyRegistration(challengeId: $challengeId, credential: $credential) {
      success
      message
      credential {
        ${PASSKEY_CREDENTIAL_FIELDS}
      }
    }
  }
`

export const DELETE_PASSKEY = gql`
  mutation deletePasskey($passkeyId: ID!) {
    deletePasskey(passkeyId: $passkeyId) {
      success
      message
    }
  }
`

export const BEGIN_PASSKEY_AUTHENTICATION = gql`
  mutation beginPasskeyAuthentication($email: String!) {
    beginPasskeyAuthentication(email: $email) {
      success
      message
      challengeId
      publicKeyOptions
    }
  }
`

export const COMPLETE_PASSKEY_AUTHENTICATION = gql`
  mutation completePasskeyAuthentication($challengeId: String!, $credential: GenericScalar!) {
    completePasskeyAuthentication(challengeId: $challengeId, credential: $credential) {
      success
      message
      auth {
        ${AUTH_SESSION_FIELDS}
      }
    }
  }
`

export const VERIFY_TOKEN = gql`
  mutation ($token: String) {
    verifyToken(token: $token) {
      payload
    }
  }
`

export const REFRESH_TOKEN = gql`
  mutation ($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      payload
      token
      refreshToken
      refreshExpiresIn
    }
  }
`

export const REQUEST_PASSWORD_RESET = gql`
  mutation requestPasswordReset($email: String!, $isInvitation: Boolean) {
    requestPasswordReset(email: $email, isInvitation: $isInvitation) {
      ok
      message
    }
  }
`

export const RESET_PASSWORD = gql`
  mutation resetPassword($uid: String!, $token: String!, $newPassword: String!) {
    resetPassword(uid: $uid, token: $token, newPassword: $newPassword) {
      ok
      message
    }
  }
`

export const FORCE_CHANGE_PASSWORD = gql`
  mutation forceChangePassword(
    $email: String!
    $currentPassword: String!
    $newPassword: String!
  ) {
    forceChangePassword(
      email: $email
      currentPassword: $currentPassword
      newPassword: $newPassword
    ) {
      ok
      message
    }
  }
`

export const START_TWO_FACTOR_SETUP = gql`
  mutation startTwoFactorSetup {
    startTwoFactorSetup {
      success
      message
      manualEntryKey
      otpauthUrl
      qrCodeSvg
      emailFallbackEnabled
    }
  }
`

export const CONFIRM_TWO_FACTOR_SETUP = gql`
  mutation confirmTwoFactorSetup($code: String!) {
    confirmTwoFactorSetup(code: $code) {
      success
      message
    }
  }
`

export const DISABLE_TWO_FACTOR = gql`
  mutation disableTwoFactor($password: String!, $code: String!) {
    disableTwoFactor(password: $password, code: $code) {
      success
      message
    }
  }
`

export const GENERATE_WS_TICKET = gql`
  mutation generateWsTicket {
    generateWsTicket {
      ticket
    }
  }
`

